/**
 * S3 Prompts tab: prompt bindings attached to this version (role, task type,
 * optional template reference). Bindings are created immediately rather than on
 * Save, because they are their own rows rather than blueprint fields.
 */

import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AiStudioPromptBinding } from '@/types/aiStudioAgent';

interface VersionPromptsTabProps {
  bindings: AiStudioPromptBinding[];
  disabled: boolean;
  adding: boolean;
  error: string | null;
  onAdd: (request: { promptRole: string; taskType: string }) => void;
}

const FIELD_CLASS =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-60';
const LABEL_CLASS = 'mb-1 block text-base font-medium text-[var(--color-surface-text)]';
const HEADER_CLASS =
  'px-3 py-2 text-left text-base font-medium text-[var(--color-surface-text-secondary)]';
const CELL_CLASS = 'px-3 py-2 text-base text-[var(--color-surface-text)]';

export const VersionPromptsTab: React.FC<VersionPromptsTabProps> = ({
  bindings,
  disabled,
  adding,
  error,
  onAdd,
}) => {
  const { t } = useLanguage();
  const [promptRole, setPromptRole] = useState('system');
  const [taskType, setTaskType] = useState('general');

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.versions.prompts.heading')}
      </h3>

      {bindings.length === 0 ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.versions.prompts.empty')}
        </p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className={HEADER_CLASS}>{t('aiStudio.versions.prompts.role')}</th>
              <th className={HEADER_CLASS}>{t('aiStudio.versions.prompts.taskType')}</th>
              <th className={HEADER_CLASS}>{t('aiStudio.versions.prompts.templateId')}</th>
            </tr>
          </thead>
          <tbody>
            {bindings.map((binding) => (
              <tr key={binding.id} className="border-b border-[var(--color-border)]">
                <td className={CELL_CLASS}>{binding.promptRole}</td>
                <td className={CELL_CLASS}>{binding.taskType}</td>
                <td className={`${CELL_CLASS} font-mono`}>
                  {binding.promptTemplateId || t('aiStudio.common.none')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!disabled && (
        <div className="grid items-end gap-3 md:grid-cols-3">
          <div>
            <label className={LABEL_CLASS} htmlFor="prompt-role">
              {t('aiStudio.versions.prompts.role')}
            </label>
            <input
              id="prompt-role"
              className={FIELD_CLASS}
              value={promptRole}
              autoComplete="off"
              onChange={(event) => setPromptRole(event.target.value)}
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="prompt-task-type">
              {t('aiStudio.versions.prompts.taskType')}
            </label>
            <input
              id="prompt-task-type"
              className={FIELD_CLASS}
              value={taskType}
              autoComplete="off"
              onChange={(event) => setTaskType(event.target.value)}
            />
          </div>
          <button
            type="button"
            disabled={adding || !promptRole.trim() || !taskType.trim()}
            onClick={() => onAdd({ promptRole: promptRole.trim(), taskType: taskType.trim() })}
            className="h-10 rounded-md border border-[var(--color-border)] px-4 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
          >
            {adding ? t('aiStudio.common.saving') : t('aiStudio.versions.prompts.add')}
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="text-base text-[var(--color-error)]">
          {error}
        </p>
      )}
    </div>
  );
};
